import type { ChangeEvent, FC, InputHTMLAttributes } from 'react'

import type { ControllerRenderProps } from 'react-hook-form'
import { useController, useFormContext } from 'react-hook-form'

import ArrowIcon from '@/assets/icons/icon-chevron-down.svg'
import { classNames } from '@/utils/helpers'

// Transformer object to handle input and output transformations for number fields
const numberTransformer = {
  input: (value: number) => {
    return isNaN(value) ? '' : value === 0 ? '0' : value.toString()
  },
  output: (event: ChangeEvent<HTMLInputElement>) => {
    const output = parseInt(event.target.value, 10)

    return isNaN(output) ? 0 : output
  },
}

interface FormFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string | React.ReactNode
  name: string
  showErrors?: boolean
  prefix?: string
  postfix?: React.ReactNode
  containerClassName?: string
  inputClassName?: string
  max?: number
  min?: number
  onValueChange?: (value: number) => void
}

export const FormNumberField: FC<FormFieldProps> = ({
  label,
  name,
  onValueChange,
  prefix,
  postfix,
  className,
  disabled,
  containerClassName,
  inputClassName,
  max = Infinity,
  min = 0,
}) => {
  const { control } = useFormContext()
  const { field, fieldState } = useController({ control, name })

  const handleChange = (value: number) => {
    field.onChange(value)
    onValueChange?.(value)
  }

  const increment = (field: ControllerRenderProps) => {
    if (disabled) {
      return
    }

    const newValue = Math.min(parseInt(field.value || 0) + 1, max)

    handleChange(newValue)
  }

  const decrement = (field: ControllerRenderProps) => {
    if (disabled) {
      return
    }

    const newValue = Math.max(parseInt(field.value || 0) - 1, min)

    handleChange(newValue)
  }

  const isMinimumValue = field.value === min
  const isMaximumValue = field.value === max

  return (
    <div className={classNames('flex flex-col gap-2 ', className)}>
      {label && (
        <label
          htmlFor={field.name}
          className="text-sm font-medium text-text-default"
        >
          {label}
        </label>
      )}

      <div
        className={classNames(
          'flex w-fit items-center rounded-md border bg-background px-3 py-1.5',
          {
            'cursor-not-allowed opacity-50': disabled,
            'border-danger-default': fieldState.error,
          },
          containerClassName,
        )}
      >
        {prefix && (
          <div className="mr-2 flex items-center">
            <span className="select-none text-sm text-secondary-300">
              {prefix}
            </span>
            <span className="my-auto ml-2 h-5 w-[1.5px] rounded bg-secondary-100 dark:bg-secondary-500">
              &nbsp;
            </span>
          </div>
        )}
        <div className="flex select-none">
          <input
            value={numberTransformer.input(field.value)}
            disabled={disabled}
            onChange={(event) => handleChange(numberTransformer.output(event))}
            className={classNames(
              'w-24 bg-inherit text-sm text-title focus:outline-none',
              inputClassName,
            )}
            type="number"
            min={0}
            max={max}
          />

          <div className="flex flex-col">
            <ArrowIcon
              type="button"
              className={classNames(
                'mb-[-5px] rotate-180 cursor-pointer hover:opacity-50 dark:text-white',
                {
                  'cursor-not-allowed pointer-events-none opacity-50':
                    isMaximumValue || disabled,
                },
              )}
              onClick={() => increment(field)}
            />

            <ArrowIcon
              type="button"
              className={classNames(
                'cursor-pointer hover:opacity-50 dark:text-white',
                {
                  'cursor-not-allowed pointer-events-none opacity-50':
                    isMinimumValue || disabled,
                },
              )}
              onClick={() => decrement(field)}
            />
          </div>
        </div>
        {postfix && (
          <div className="ml-2 flex items-center select-none">
            <span className="my-auto mr-2 h-5 w-[1.5px] rounded bg-secondary-100 dark:bg-secondary-500">
              &nbsp;
            </span>
            <span className="select-none text-sm text-secondary-300">
              {postfix}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}
